import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  UserNoticeSendRequest,
  UserNoticeSendResponse,
  UserNoticeDetailRequest,
  UserNoticeDetailResponse,
  UserNoticeReplySaveRequest,
  UserNoticeReplySaveResponse,
  // UserNoticeReplyRequest,
  UserNoticeReplyResponse,
  UserNoticeReadAllResponse,
  UserNoticeHasNewReplyResponse,
} from "~/types/common";

export interface NoticeDataType {
  type: string;
  message: string;
}

export const NOTICE_QUERY_KEYS = {
  alert: ["notice", "alert", "list", "send-sub-data"],
} as const;

const getAlertList = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USERNOTICE.NEWNOTICELIST
    );
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useAlertList = () => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.alert],
    queryFn: () => getAlertList(),
    select: (data) => data,
  });
};

const getNoticeSenderList = async (request: any) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USERNOTICE.NOTICE_SENDER_LIST,
      { params: request }
    );
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useNoticeSenderList = (request: any) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.alert, request],
    queryFn: () => getNoticeSenderList(request),
    select: (data) => data,
  });
};

const getNoticeReceiverList = async (request: any) => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USERNOTICE.NOTICE_RECIPIENT_LIST,
      { params: request }
    );
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useNoticeReceiverList = (request: any) => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.alert, request],
    queryFn: () => getNoticeReceiverList(request),
    select: (data) => data,
  });
};

const getNoticeSendSubData = async () => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USERNOTICE.NOTICE_SEND_SUBDATA
    );
    return data.data;
  } catch (error) {
    console.error("API 호출 에러:", error);
    throw error;
  }
};

export const useNoticeSendSubData = () => {
  return useQuery({
    queryKey: [...NOTICE_QUERY_KEYS.alert, "send-sub-data"],
    queryFn: () => getNoticeSendSubData(),
    select: (data) => data,
  });
};

// User Notice 전송
const sendUserNotice = async (
  requestData: UserNoticeSendRequest
): Promise<UserNoticeSendResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.USERNOTICE.SEND,
      requestData
    );
    return data;
  } catch (error) {
    console.error("사용자 알림 전송 API 호출 에러:", error);
    throw error;
  }
};

export const useUserNoticeSend = () => {
  return useMutation({
    mutationFn: sendUserNotice,
  });
};

// User Notice 상세 조회
const getUserNoticeDetail = async (
  params: UserNoticeDetailRequest
): Promise<UserNoticeDetailResponse> => {
  try {
    const { data } = await privateHttp.get(
      API_ENDPOINTS.USERNOTICE.NOTICE_READ,
      { params }
    );
    return data;
  } catch (error) {
    console.error("사용자 알림 상세 조회 API 호출 에러:", error);
    throw error;
  }
};

export const useUserNoticeDetail = (params: UserNoticeDetailRequest) => {
  return useQuery({
    queryKey: ["user-notice-detail", params],
    queryFn: () => getUserNoticeDetail(params),
    enabled: !!params.notiRecvSeq, // notiRecvSeq가 있을 때만 API 호출
  });
};

// User Notice 댓글 저장
const saveUserNoticeReply = async (
  requestData: UserNoticeReplySaveRequest
): Promise<UserNoticeReplySaveResponse> => {
  try {
    const { data } = await privateHttp.post(
      API_ENDPOINTS.USERNOTICE.REPLY_SAVE,
      requestData
    );
    return data;
  } catch (error) {
    console.error("사용자 알림 댓글 저장 API 호출 에러:", error);
    throw error;
  }
};

export const useUserNoticeReplySave = () => {
  return useMutation({
    mutationFn: saveUserNoticeReply,
  });
};

// User Notice 댓글 조회
const getUserNoticeReply = async (
  notiRecvSeq: number
): Promise<UserNoticeReplyResponse> => {
  try {
    const { data } = await privateHttp.get<UserNoticeReplyResponse>(
      `${API_ENDPOINTS.USERNOTICE.REPLY}?notiRecvSeq=${notiRecvSeq}`
    );
    return data;
  } catch (error) {
    console.error("사용자 알림 댓글 조회 API 호출 에러:", error);
    throw error;
  }
};

export const useUserNoticeReply = (notiRecvSeq: number) => {
  return useQuery({
    queryKey: ["user-notice-reply", notiRecvSeq],
    queryFn: () => getUserNoticeReply(notiRecvSeq),
    enabled: !!notiRecvSeq,
  });
};

// User Notice 전체 읽음 처리
const readAllUserNotice = async (): Promise<UserNoticeReadAllResponse> => {
  try {
    const { data } = await privateHttp.patch(API_ENDPOINTS.USERNOTICE.READ_ALL);
    return data;
  } catch (error) {
    console.error("사용자 알림 전체 읽음 처리 API 호출 에러:", error);
    throw error;
  }
};

export const useUserNoticeReadAll = () => {
  return useMutation({
    mutationFn: readAllUserNotice,
  });
};

// User Notice Has New Reply API 함수
const getUserNoticeHasNewReply =
  async (): Promise<UserNoticeHasNewReplyResponse> => {
    try {
      const { data } = await privateHttp.get<UserNoticeHasNewReplyResponse>(
        API_ENDPOINTS.USERNOTICE.HAS_NEW_REPLY
      );
      return data;
    } catch (error) {
      console.error("User Notice Has New Reply API 호출 에러:", error);
      throw error;
    }
  };

// User Notice Has New Reply React Query Hook
export const useUserNoticeHasNewReply = () => {
  return useQuery({
    queryKey: ["user-notice-has-new-reply"],
    queryFn: getUserNoticeHasNewReply,
    staleTime: 0, // 즉시 stale 상태로 만들어 매번 호출
    gcTime: 0, // 캐시하지 않음
    refetchOnMount: true, // 컴포넌트 마운트 시 항상 refetch
    refetchOnWindowFocus: true, // 윈도우 포커스 시 refetch
  });
};
